import { ReactNode, memo, useCallback, useMemo } from "react";
import Draggable from "react-draggable";

import MuiDialog from "@mui/material/Dialog";
import DialogContent, { DialogContentProps } from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import type { PaperProps } from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { Breakpoint, SxProps, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { CloseIcon } from "components/icons";

interface DialogComponentProps {
  readonly title?: string;
  readonly subtitle?: ReactNode;
  readonly titleTextSx?: SxProps;
  readonly open: boolean;
  readonly children: ReactNode;
  readonly onClose: (event: object, reason: string) => void;
  readonly dialogContentProps?: DialogContentProps;
  readonly minWidth?: number;
  readonly maxWidth?: Breakpoint;
  readonly sx?: SxProps;
  readonly paperContentSx?: SxProps;
  readonly dialogTitleSx?: SxProps;
  readonly isDraggable?: boolean;
  readonly hideBackdrop?: boolean;
  readonly disableEnforceFocus?: boolean;
  readonly disableScrollLock?: boolean;
  readonly extraTitleButton?: ReactNode;
}

function Dialog({
  title,
  subtitle,
  titleTextSx,
  open,
  children,
  dialogContentProps,
  paperContentSx,
  dialogTitleSx,
  onClose,
  minWidth = 586,
  maxWidth,
  isDraggable,
  hideBackdrop,
  disableEnforceFocus,
  disableScrollLock,
  extraTitleButton,
  sx
}: DialogComponentProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const isTablet = useMediaQuery(theme.breakpoints.down("tablet"));
  const { typography } = useTheme();

  // Memoize the close button click handler
  const handleCloseClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => onClose(e, "closeButton"),
    [onClose]
  );

  // Memoize the DialogTitle sx object to prevent recreation on every render
  const dialogTitleSxMemo = useMemo(
    () =>
      ({
        alignItems: "center",
        backgroundColor: grey[200],
        ...(isDraggable && { cursor: "move" }),
        "&:hover": isDraggable ? { background: grey[300] } : {},
        pointerEvents: "auto",
        ...dialogTitleSx
      }) as SxProps,
    [isDraggable, dialogTitleSx]
  );

  // Memoize the Paper sx object
  const paperSxMemo = useMemo(
    () =>
      ({
        minWidth: isMobile ? "calc(100% - 40px)" : typography.pxToRem(minWidth),
        ...(isMobile && { width: "calc(100% - 40px)" }),
        ...(isTablet && !isMobile && { width: typography.pxToRem(minWidth) }),
        maxWidth: "100%",
        borderRadius: "12px",
        pointerEvents: "auto",
        ...paperContentSx
      }) as any,
    [isMobile, isTablet, typography, minWidth, paperContentSx]
  );

  const PaperComponent = useCallback(
    (props: PaperProps) => {
      return (
        <Draggable
          axis={isDraggable ? undefined : "none"}
          handle=".draggable-dialog-title"
          cancel={'[class*="MuiDialogContent-root"]'}
        >
          <Paper {...props} sx={paperSxMemo} />
        </Draggable>
      );
    },
    [isDraggable, paperSxMemo]
  );

  return (
    <MuiDialog
      sx={sx}
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      PaperComponent={PaperComponent}
      hideBackdrop={hideBackdrop}
      disableEnforceFocus={disableEnforceFocus}
      disableScrollLock={disableScrollLock}
    >
      <DialogTitle sx={dialogTitleSxMemo} className="draggable-dialog-title">
        <Stack justifyContent="space-between" direction="row" width="100%">
          {Boolean(title) && (
            <Stack>
              <Typography
                data-testid="dialog-title"
                variant="body2"
                sx={{
                  pl: 1,
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: 1.4,
                  ...titleTextSx
                }}
              >
                {title}
              </Typography>
              {Boolean(subtitle) && (
                <Typography
                  data-testid="dialog-subtitle"
                  sx={{ pl: 1, mt: 0.25, fontSize: 12, color: grey[600] }}
                >
                  {subtitle}
                </Typography>
              )}
            </Stack>
          )}
          <Stack direction="row" columnGap={2}>
            {extraTitleButton}
            <IconButton
              onClick={handleCloseClick}
              onTouchStart={handleCloseClick}
              sx={{ marginLeft: "auto" }}
              data-testid="close-dialog"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent data-testid="dialog-content" {...dialogContentProps}>
        {children}
      </DialogContent>
    </MuiDialog>
  );
}

// Memoize the Dialog component to prevent unnecessary re-renders when props haven't changed
export default memo(Dialog);
